/**
 * Syncs Infinite Options selections into Simple Bundles line-item properties.
 * Updates properties[optionName] and properties[_bundle_selection] (values joined by " <> ").
 * Also swaps the component row thumbnail when a selected option has data-option-image.
 */
class BundleInfiniteOptions extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #bundleSelectionInput = null;

  connectedCallback() {
    this.#bundleSelectionInput = this.querySelector('[data-bundle-io-bundle-selection]');
    this.addEventListener('change', this.#onChange);
    this.#syncAll();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  /**
   * @param {Event} event
   */
  #onChange = (event) => {
    const target = /** @type {HTMLInputElement | null} */ (event.target);
    if (!target?.matches('[data-bundle-io-value]')) return;
    if (target.disabled || target.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      target.checked = false;
      return;
    }

    this.#applySelection(target);
    this.#syncBundleSelection();
  };

  #syncAll() {
    const fieldsets = this.querySelectorAll('[data-bundle-io-option]');
    fieldsets.forEach((fieldset) => {
      const selected = /** @type {HTMLInputElement | null} */ (
        fieldset.querySelector('[data-bundle-io-value]:checked:not([disabled])')
      );
      if (selected) this.#applySelection(selected);
    });
    this.#syncBundleSelection();
  }

  /**
   * @param {HTMLInputElement} input
   */
  #applySelection(input) {
    const optionName = input.dataset.optionName;
    if (!optionName) return;

    const propertyInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`[data-bundle-io-property][data-option-name="${CSS.escape(optionName)}"]`)
    );
    if (propertyInput) {
      propertyInput.value = input.value;
      propertyInput.removeAttribute('required');
      propertyInput.removeAttribute('data-bundle-io-required');
      propertyInput.readOnly = true;
      propertyInput.setCustomValidity('');
    }

    const fieldset = input.closest('[data-bundle-io-option]');
    const selectedLabel = fieldset?.querySelector('[data-bundle-io-selected-label]');
    if (selectedLabel) selectedLabel.textContent = input.dataset.optionLabel || input.value;

    const row = input.closest('.bundle-component');
    const imageUrl = input.dataset.optionImage;
    if (row && imageUrl) {
      const img = /** @type {HTMLImageElement | null} */ (row.querySelector('.bundle-component__image'));
      if (img && img.tagName === 'IMG') {
        img.src = imageUrl;
        img.srcset = '';
      }
    }
  }

  #syncBundleSelection() {
    if (!this.#bundleSelectionInput) return;

    const propertyInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('[data-bundle-io-property]')
    );
    const values = [...propertyInputs].map((input) => input.value);
    this.#bundleSelectionInput.value = values.join(' <> ');
  }
}

if (!customElements.get('bundle-infinite-options')) {
  customElements.define('bundle-infinite-options', BundleInfiniteOptions);
}
